import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/utils';
import {
    Search, Users, UserPlus, ChevronDown, ChevronUp,
    Mail, Calendar, ExternalLink, RefreshCw,
    ChevronLeft, ChevronRight, Globe, Fish,
    Smartphone, Monitor, ChevronsLeft, ChevronsRight,
    Crown, Star, Zap, ArrowUpDown,
} from 'lucide-react';
import {
    useAdminListUsers,
    useAdminUserStats,
    type AdminUserProfile,
    type SortField,
} from '@/hooks/useAdminUsers';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// ─── Constants ──────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const TIER_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; badgeVariant: 'success' | 'warning' | 'info' | 'neutral' }> = {
    premium: { label: 'Premium', icon: <Crown size={12} />, color: 'text-amber-400', badgeVariant: 'warning' },
    pro: { label: 'Pro', icon: <Star size={12} />, color: 'text-ocean-400', badgeVariant: 'info' },
    free: { label: 'Basic', icon: <Zap size={12} />, color: 'text-muted-foreground', badgeVariant: 'neutral' },
};

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
    android: <Smartphone size={13} className="text-emerald-400" />,
    ios: <Smartphone size={13} className="text-blue-400" />,
    web: <Monitor size={13} className="text-purple-400" />,
    windows: <Monitor size={13} className="text-sky-400" />,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getTierConfig(tier: string | null | undefined) {
    return TIER_CONFIG[tier || 'free'] || TIER_CONFIG.free;
}

function formatRelativeDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    try {
        return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: es });
    } catch {
        return '—';
    }
}

// ─── Custom useDebounce Hook (KISS) ─────────────────────────────────────────

function useDebounce(value: string, delayMs: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);
    return debounced;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function UsersPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<SortField>('created_at');
    const [sortAsc, setSortAsc] = useState(false);
    const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

    const debouncedSearch = useDebounce(searchQuery, 350);

    // Reset to page 1 when search or sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, sortField, sortAsc]);

    // Data
    const { data: stats, isLoading: statsLoading } = useAdminUserStats();
    const {
        data: result,
        isLoading: usersLoading,
        isFetching,
        refetch,
    } = useAdminListUsers({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        sortBy: sortField,
        sortAsc,
    });

    const users = result?.users ?? [];
    const totalCount = result?.totalCount ?? 0;
    const totalPages = result?.totalPages ?? 1;

    // Sort toggle
    const toggleSort = useCallback((field: SortField) => {
        if (sortField === field) {
            setSortAsc(prev => !prev);
        } else {
            setSortField(field);
            setSortAsc(true);
        }
    }, [sortField]);

    // Pagination
    const goToPage = useCallback((page: number) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
        setExpandedUserId(null);
    }, [totalPages]);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Usuarios"
                description={`Gestión de usuarios — ${stats?.total_users?.toLocaleString('es-ES') || '...'} registrados`}
            >
                <button
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={14} className={cn(isFetching && 'animate-spin')} />
                    Actualizar
                </button>
            </PageHeader>

            {/* Stats Cards */}
            <StatsCards stats={stats} statsLoading={statsLoading} totalCount={totalCount} />

            {/* Search */}
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    placeholder="Buscar por nombre, email o país..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                />
                {isFetching && (
                    <RefreshCw size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
                )}
            </div>

            {/* Users Table */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="overflow-x-auto max-h-[calc(100vh-340px)] overflow-y-auto scrollbar-thin">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10">
                            <tr className="border-b border-border bg-card">
                                <th className="w-12 px-3 py-3 bg-card" />
                                <SortableHeader field="display_name" label="Nombre" current={sortField} asc={sortAsc} onSort={toggleSort} />
                                <SortableHeader field="email" label="Email" current={sortField} asc={sortAsc} onSort={toggleSort} />
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">País</th>
                                <SortableHeader field="subscription_tier" label="Plan" current={sortField} asc={sortAsc} onSort={toggleSort} />
                                <SortableHeader field="captures_count" label="Capturas" current={sortField} asc={sortAsc} onSort={toggleSort} align="right" />
                                <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">Plataforma</th>
                                <SortableHeader field="created_at" label="Registro" current={sortField} asc={sortAsc} onSort={toggleSort} />
                                <SortableHeader field="last_seen" label="Actividad" current={sortField} asc={sortAsc} onSort={toggleSort} />
                                <th className="w-10 px-3 py-3 bg-card" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {usersLoading && users.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="py-16 text-center text-muted-foreground">
                                        <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                                        Cargando usuarios...
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="py-16 text-center text-muted-foreground">
                                        <Search size={24} className="mx-auto mb-2 text-muted-foreground/40" />
                                        No se encontraron usuarios
                                        {debouncedSearch && <> para "{debouncedSearch}"</>}
                                    </td>
                                </tr>
                            ) : (
                                users.map(user => (
                                    <UserRow
                                        key={user.user_id}
                                        user={user}
                                        isExpanded={expandedUserId === user.user_id}
                                        onToggle={() => setExpandedUserId(
                                            expandedUserId === user.user_id ? null : user.user_id
                                        )}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {totalCount > 0 && (
                    <PaginationFooter
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        pageSize={PAGE_SIZE}
                        isFetching={isFetching}
                        onPageChange={goToPage}
                    />
                )}
            </div>
        </div>
    );
}

// ─── Stats Cards (SRP) ─────────────────────────────────────────────────────

function StatsCards({ stats, statsLoading, totalCount }: {
    stats: { total_users: number; users_with_display_name: number } | undefined;
    statsLoading: boolean;
    totalCount: number;
}) {
    const ratio = stats && stats.total_users > 0
        ? ((stats.users_with_display_name / stats.total_users) * 100).toFixed(1)
        : null;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
                icon={<Users size={20} />}
                iconClass="bg-ocean-400/10 text-ocean-400"
                label="Total Usuarios"
                value={statsLoading ? '...' : stats?.total_users?.toLocaleString('es-ES') || '0'}
            />
            <StatCard
                icon={<UserPlus size={20} />}
                iconClass="bg-teal-500/10 text-teal-500"
                label="Con Nombre"
                value={statsLoading ? '...' : stats?.users_with_display_name?.toLocaleString('es-ES') || '0'}
            />
            <StatCard
                icon={<Mail size={20} />}
                iconClass="bg-purple-400/10 text-purple-400"
                label="Ratio Nombre"
                value={ratio ? `${ratio}%` : '—'}
            />
        </div>
    );
}

function StatCard({ icon, iconClass, label, value }: {
    icon: React.ReactNode;
    iconClass: string;
    label: string;
    value: string;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-4">
            <div className={cn('p-2.5 rounded-lg', iconClass)}>{icon}</div>
            <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-2xl font-bold text-foreground">{value}</p>
            </div>
        </div>
    );
}

// ─── Sortable Header (DRY) ──────────────────────────────────────────────────

function SortableHeader({ field, label, current, asc, onSort, align = 'left' }: {
    field: SortField;
    label: string;
    current: SortField;
    asc: boolean;
    onSort: (field: SortField) => void;
    align?: 'left' | 'right' | 'center';
}) {
    const isActive = current === field;
    const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : '';

    return (
        <th className={cn('px-4 py-3 bg-card', align === 'right' ? 'text-right' : 'text-left')}>
            <button
                onClick={() => onSort(field)}
                className={cn(
                    'flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors',
                    isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                    alignClass,
                )}
            >
                {label}
                {isActive ? (
                    asc ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                ) : (
                    <ArrowUpDown size={10} className="text-muted-foreground/40" />
                )}
            </button>
        </th>
    );
}

// ─── User Row ───────────────────────────────────────────────────────────────

function UserRow({ user, isExpanded, onToggle }: {
    user: AdminUserProfile;
    isExpanded: boolean;
    onToggle: () => void;
}) {
    const navigate = useNavigate();
    const createdDate = user.created_at ? new Date(user.created_at) : null;
    const tier = getTierConfig(user.subscription_tier);
    const platform = user.platform || null;
    const [avatarError, setAvatarError] = useState(false);

    return (
        <>
            <tr
                className={cn(
                    'cursor-pointer transition-colors',
                    isExpanded ? 'bg-muted/20' : 'hover:bg-muted/10',
                )}
                onClick={onToggle}
            >
                {/* Avatar */}
                <td className="px-3 py-2.5">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0">
                        {user.avatar_url && !avatarError ? (
                            <img
                                src={user.avatar_url}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={() => setAvatarError(true)}
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <span className="text-xs font-bold text-muted-foreground">
                                {(user.display_name || user.email || '?')[0]?.toUpperCase()}
                            </span>
                        )}
                    </div>
                </td>

                {/* Name */}
                <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-foreground truncate max-w-[180px]">
                        {user.display_name || <span className="text-muted-foreground italic">Sin nombre</span>}
                    </p>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/users/${user.user_id}`);
                        }}
                        className="text-[10px] text-primary hover:underline flex items-center gap-0.5 mt-0.5"
                    >
                        Ver perfil <ExternalLink size={8} />
                    </button>
                </td>

                {/* Email */}
                <td className="px-4 py-2.5">
                    <p className="text-sm text-muted-foreground truncate max-w-[220px]">{user.email || '—'}</p>
                </td>

                {/* Country */}
                <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                        {user.country_code ? (
                            <>
                                <img
                                    src={`https://flagcdn.com/w80/${user.country_code.toLowerCase()}.png`}
                                    srcSet={`https://flagcdn.com/w160/${user.country_code.toLowerCase()}.png 2x`}
                                    alt={user.country_code}
                                    className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                                    loading="lazy"
                                />
                                <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                                    {user.country_name || user.country_code}
                                </span>
                            </>
                        ) : (
                            <span className="text-xs text-muted-foreground/50">—</span>
                        )}
                    </div>
                </td>

                {/* Subscription Tier */}
                                <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {user.banned_at && (
                                            <StatusBadge status="BANEADO" variant="error" />
                                        )}
                                        <StatusBadge
                                            status={tier.label}
                                            variant={tier.badgeVariant}
                                        />
                                    </div>
                                </td>

                {/* Captures Count */}
                <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                        <Fish size={13} className={cn(
                            (user.captures_count ?? 0) > 0 ? 'text-ocean-400' : 'text-muted-foreground/30'
                        )} />
                        <span className={cn(
                            'text-sm font-medium tabular-nums',
                            (user.captures_count ?? 0) > 0 ? 'text-foreground' : 'text-muted-foreground/50',
                        )}>
                            {user.captures_count ?? 0}
                        </span>
                    </div>
                </td>

                {/* Platform */}
                <td className="px-4 py-2.5 text-center">
                    {platform ? (
                        <span className="inline-flex items-center gap-1" title={platform}>
                            {PLATFORM_ICONS[platform] || <Globe size={13} className="text-muted-foreground" />}
                            <span className="text-[10px] uppercase text-muted-foreground font-medium">{platform}</span>
                        </span>
                    ) : (
                        <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                </td>

                {/* Created At */}
                <td className="px-4 py-2.5">
                    <p className="text-xs text-muted-foreground whitespace-nowrap">
                        {createdDate ? format(createdDate, 'dd MMM yy', { locale: es }) : '—'}
                    </p>
                </td>

                {/* Last Activity (from analytics_events) */}
                <td className="px-4 py-2.5">
                    <p className="text-xs text-muted-foreground whitespace-nowrap" title={user.last_seen || ''}>
                        {formatRelativeDate(user.last_seen)}
                    </p>
                </td>

                {/* Expand */}
                <td className="px-3 py-2.5 text-center">
                    <ChevronDown
                        size={14}
                        className={cn(
                            'text-muted-foreground transition-transform',
                            isExpanded && 'rotate-180'
                        )}
                    />
                </td>
            </tr>

            {/* Expanded Details */}
            {isExpanded && (
                <tr>
                    <td colSpan={10} className="px-4 pb-4 pt-2 bg-muted/10">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <DetailItem icon={<Mail size={13} />} label="Email" value={user.email || 'N/A'} />
                            <DetailItem icon={<Globe size={13} />} label="Región" value={user.region || 'N/A'} />
                            <DetailItem
                                icon={<Calendar size={13} />}
                                label="Registrado"
                                value={createdDate ? format(createdDate, "d 'de' MMMM yyyy", { locale: es }) : 'N/A'}
                            />
                            <DetailItem
                                icon={<ExternalLink size={13} />}
                                label="User ID"
                                value={user.user_id}
                                copyable
                            />
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/users/${user.user_id}`);
                                }}
                                className="px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                            >
                                <Users size={14} />
                                Ver Perfil Completo
                            </button>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// ─── Detail Item (SRP) ──────────────────────────────────────────────────────

function DetailItem({ icon, label, value, copyable }: {
    icon: React.ReactNode;
    label: string;
    value: string;
    copyable?: boolean;
}) {
    const handleCopy = () => {
        if (copyable) navigator.clipboard.writeText(value);
    };

    return (
        <div
            className={cn('flex items-start gap-2 text-sm', copyable && 'cursor-pointer hover:opacity-80')}
            onClick={handleCopy}
            title={copyable ? 'Clic para copiar' : undefined}
        >
            <span className="text-muted-foreground mt-0.5 shrink-0">{icon}</span>
            <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold">{label}</p>
                <p className="text-foreground truncate">{value}</p>
            </div>
        </div>
    );
}

// ─── Pagination Footer (SRP) ────────────────────────────────────────────────

function PaginationFooter({ currentPage, totalPages, totalCount, pageSize, isFetching, onPageChange }: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    isFetching: boolean;
    onPageChange: (page: number) => void;
}) {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalCount);

    // Generate page numbers to show
    const pages = generatePageNumbers(currentPage, totalPages);

    return (
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
                {start}–{end} de {totalCount.toLocaleString('es-ES')} usuarios
            </span>

            <div className="flex items-center gap-1">
                {/* First page */}
                <PaginationButton
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1 || isFetching}
                    title="Primera página"
                >
                    <ChevronsLeft size={14} />
                </PaginationButton>

                {/* Previous */}
                <PaginationButton
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1 || isFetching}
                    title="Anterior"
                >
                    <ChevronLeft size={14} />
                </PaginationButton>

                {/* Page numbers */}
                {pages.map((page, idx) =>
                    page === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-1 text-muted-foreground/40 text-xs">…</span>
                    ) : (
                        <PaginationButton
                            key={page}
                            onClick={() => onPageChange(page as number)}
                            disabled={isFetching}
                            active={currentPage === page}
                        >
                            {page}
                        </PaginationButton>
                    )
                )}

                {/* Next */}
                <PaginationButton
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || isFetching}
                    title="Siguiente"
                >
                    <ChevronRight size={14} />
                </PaginationButton>

                {/* Last page */}
                <PaginationButton
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages || isFetching}
                    title="Última página"
                >
                    <ChevronsRight size={14} />
                </PaginationButton>
            </div>
        </div>
    );
}

function PaginationButton({ children, onClick, disabled, active, title }: {
    children: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    active?: boolean;
    title?: string;
}) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={cn(
                'min-w-[28px] h-7 flex items-center justify-center rounded-md text-xs font-medium transition-colors',
                active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                disabled && !active && 'opacity-30 cursor-not-allowed hover:bg-transparent',
            )}
        >
            {children}
        </button>
    );
}

/**
 * Generate page numbers with ellipsis for large page counts.
 * E.g. [1, '...', 4, 5, 6, '...', 19] for currentPage=5, totalPages=19
 */
function generatePageNumbers(current: number, total: number): (number | '...')[] {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [];

    // Always show first page
    pages.push(1);

    // Left ellipsis
    if (current > 3) pages.push('...');

    // Pages around current
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    // Right ellipsis
    if (current < total - 2) pages.push('...');

    // Always show last page
    if (total > 1) pages.push(total);

    return pages;
}
